import React from "react";
import styled from "styled-components";
import {
  APP_NAME,
  APP_SUPPORT_EMAIL,
  APP_URL,
  FULL_COMPANY_NAME,
  TAGLINE,
} from "../helpers/constants";
import { Spacer } from "../styles/CommonElements";
import SEO from "../components/SEO";

export default function PrivacyPolicy() {
  return (
    <Container>
      <SEO
        title={`Privacy Policy | ${APP_NAME} - ${TAGLINE}`}
        description={`${APP_NAME} Privacy Policy. How we collect, store, and protect your project data, uploaded media, phone number, and SMS consent information under TCPA and applicable privacy law.`}
        keywords={`${APP_NAME} privacy, TCPA SMS consent, phone number data protection, contractor dispatch notifications, no third-party sharing, data security`}
        imageAlt={`${APP_NAME} Privacy Policy`}
        category="Legal"
        tags={[
          "Clumse privacy policy",
          "TCPA SMS consent",
          "contractor dispatch privacy",
          "no third-party data sharing",
        ]}
      />
      <MainHeader>Privacy Policy</MainHeader>
      <Spacer $marginTop="2rem" />
      <TextContainer>

        <TextSection>
          {APP_NAME} Services ("{APP_NAME}" or "Services") are provided by{" "}
          {FULL_COMPANY_NAME} ("We", "Our", "Company") to you ("User",
          "Homeowner", "Landlord", or "Contractor"), collectively referred to
          as "Users."
        </TextSection>
        <TextSection>
          We are committed to transparency about how your information is
          collected, used, stored, and protected. Please read this policy
          carefully. By accessing or using the Services, you agree to the
          data practices described here.
        </TextSection>
        <TextSection>
          <strong>Last Updated:</strong> June 9, 2026
        </TextSection>

        {/* ── Section 1 ── */}
        <Header>1. Information We Collect</Header>
        <TextSection>
          We collect the following categories of information when you use the
          {APP_NAME} platform:
        </TextSection>
        <List>
          <ListItem>
            <strong>Account and Identity Information:</strong> Your name,
            email address, mobile phone number, and account credentials
            provided during registration.
          </ListItem>
          <ListItem>
            <strong>Project and Property Information:</strong> Descriptions
            of repair, renovation, or maintenance projects entered through our
            AI intake chat, including room type, project scope, finish
            preferences, ZIP code, property type, occupancy status, timeline,
            and related project details.
          </ListItem>
          <ListItem>
            <strong>Uploaded Media and Photos:</strong> Images and photographs
            you voluntarily upload through the chat interface to support scope
            assessment and AI-generated visual renderings.
          </ListItem>
          <ListItem>
            <strong>Payment Information:</strong> Transaction records for
            search execution fees processed through our third-party payment
            processor (Stripe). We do not store full card numbers on our
            servers.
          </ListItem>
          <ListItem>
            <strong>Usage Data:</strong> Log files, IP addresses, browser
            type, device identifiers, page views, and interaction data
            collected automatically as you use the platform.
          </ListItem>
          <ListItem>
            <strong>Communications:</strong> Messages, feedback, and support
            requests you send to us directly.
          </ListItem>
        </List>

        {/* ── Section 2 ── */}
        <Header>2. How We Use Your Information</Header>
        <TextSection>
          We use the information we collect for the following purposes:
        </TextSection>
        <List>
          <ListItem>
            To operate and deliver the Services, including compiling
            pre-scoped contractor data packets, routing dispatch notifications
            to local independent tradesmen, and managing the multi-bid search
            process on your behalf.
          </ListItem>
          <ListItem>
            To send you operational communications, including project status
            updates, bid notifications, confirmation emails, and platform
            alerts related to your active job search.
          </ListItem>
          <ListItem>
            To send SMS text message notifications to contractors and property
            owners as part of the automated dispatch and bid coordination
            system (see Section 3 for full TCPA disclosure).
          </ListItem>
          <ListItem>
            To improve, develop, and personalize our AI intake engine, scope
            generation algorithms, and platform features based on aggregated
            and anonymized usage patterns.
          </ListItem>
          <ListItem>
            To process payments for search execution fees and maintain
            transaction records.
          </ListItem>
          <ListItem>
            To comply with applicable legal obligations, enforce our Terms of
            Service, and protect the rights, safety, and property of {APP_NAME},
            our Users, and the public.
          </ListItem>
        </List>

        {/* ── Section 3 ── */}
        <Header>3. SMS and Text Message Communications: TCPA Consent Disclosure</Header>
        <TextSection>
          <strong>Explicit written consent.</strong> By providing your mobile
          telephone number and completing registration on the {APP_NAME}{" "}
          platform, you provide your <strong>explicit prior written consent</strong>{" "}
          to receive automated operational notifications, localized contractor
          dispatch text messages, project scope updates, bid alerts, and
          platform status communications delivered directly from our automated
          messaging system to the mobile number you provide. This consent
          satisfies the requirements of the Telephone Consumer Protection
          Act (TCPA), 47 U.S.C. § 227, and its implementing regulations.
        </TextSection>
        <TextSection>
          <strong>Message frequency.</strong> Message frequency varies based
          on your level of platform activity and the number of active job
          searches you initiate. You may receive notifications for each
          dispatch event, bid submission, and status change associated with
          your project.
        </TextSection>
        <TextSection>
          <strong>Message and data rates.</strong> Standard message and data
          rates may apply depending on your mobile carrier and plan. We are
          not responsible for charges imposed by your carrier.
        </TextSection>
        <TextSection>
          <strong>Opt-out.</strong> You may opt out of SMS communications at
          any time by replying STOP to any text message you receive from us,
          or by contacting us at{" "}
          <a href={`mailto:${APP_SUPPORT_EMAIL}`}>{APP_SUPPORT_EMAIL}</a>.
          After opting out, you may continue to receive a single confirmation
          message. Note that opting out of SMS may affect the timely delivery
          of contractor bid alerts.
        </TextSection>
        <TextSection>
          <strong>Contractor dispatch notifications.</strong> When a property
          owner initiates a paid bid search, our system automatically
          transmits pre-scoped project data packets via SMS and email to
          qualified local contractors in the relevant ZIP code and service
          category. Contractors who receive these notifications have
          independently opted in to receive dispatch notifications from{" "}
          {APP_NAME} as part of their contractor registration.
        </TextSection>

        {/* ── Section 4 ── */}
        <Header>4. Data Security and Storage</Header>
        <TextSection>
          <strong>Secure storage of project data.</strong> User-provided
          project descriptions, conversational intake data, uploaded
          photographs and media, structural notes, and AI-generated scope
          documents are stored securely within our cloud infrastructure to
          compile and deliver the contractor bid package. We implement
          industry-standard technical and organizational security measures,
          including encryption in transit (TLS) and encryption at rest, access
          controls, and regular security assessments to protect your data
          against unauthorized access, disclosure, alteration, or destruction.
        </TextSection>
        <TextSection>
          <strong>No sale of phone numbers or SMS opt-in data.</strong>{" "}
          Mobile phone numbers and explicit text messaging opt-in data
          collected through the {APP_NAME} platform will{" "}
          <strong>never be sold, leased, rented, or shared</strong> with
          third parties, affiliates, data brokers, or any other entity for
          marketing, promotional, or advertising purposes under any
          circumstances. Phone numbers are used solely for operational
          platform communications as described in this policy.
        </TextSection>
        <TextSection>
          <strong>No third-party sharing for marketing.</strong> We do not
          sell, trade, or otherwise transfer your personally identifiable
          information, project data, or uploaded media to outside parties for
          commercial or marketing purposes. Project data shared with
          independent contractors is limited strictly to the pre-scoped
          information necessary for them to prepare a competitive bid on your
          specific project and is transmitted only after you initiate a paid
          search.
        </TextSection>

        {/* ── Section 5 ── */}
        <Header>5. Information Shared With Third Parties</Header>
        <TextSection>
          We share your information only in the following limited and specific
          circumstances:
        </TextSection>
        <List>
          <ListItem>
            <strong>Contractor Dispatch (with your authorization):</strong>{" "}
            When you initiate a paid bid search, we transmit your pre-scoped
            project data packet, including project type, scope description,
            ZIP code, and relevant property details, to local licensed
            contractors in our network. Your full name, street address, and
            personal contact information are not disclosed to contractors
            until you explicitly choose to share them after receiving a bid.
          </ListItem>
          <ListItem>
            <strong>Payment Processing:</strong> We share transaction data
            with Stripe, Inc. for the purpose of processing your search
            execution fee. Stripe&apos;s privacy policy governs their handling
            of payment information.
          </ListItem>
          <ListItem>
            <strong>Service Providers:</strong> We may share data with
            trusted infrastructure and technology vendors (e.g., cloud
            hosting, analytics, email delivery) who process data on our
            behalf under strict confidentiality obligations and data processing
            agreements.
          </ListItem>
          <ListItem>
            <strong>Legal Compliance:</strong> We may disclose information
            when required to do so by law, subpoena, court order, or
            governmental authority, or when we believe in good faith that
            disclosure is necessary to protect the rights, safety, or property
            of {APP_NAME}, our Users, or the public.
          </ListItem>
        </List>

        {/* ── Section 6 ── */}
        <Header>6. Cookies and Tracking Technologies</Header>
        <TextSection>
          We use cookies and similar tracking technologies to operate the
          platform, remember your preferences, maintain your session, and
          analyze aggregate usage patterns. You may control cookie settings
          through your browser; however, disabling certain cookies may affect
          the functionality of the Services. We do not use cookies to build
          advertising profiles or sell data to advertising networks.
        </TextSection>

        {/* ── Section 7 ── */}
        <Header>7. Data Retention</Header>
        <TextSection>
          We retain your account and project data for as long as your account
          is active or as needed to provide the Services. After account
          deletion, we may retain certain records for a period required by
          law or legitimate business purposes (e.g., transaction records for
          tax compliance, dispute resolution). Uploaded photos and AI-generated
          renders may be deleted from active storage within 90 days of your
          account deletion request unless otherwise required.
        </TextSection>

        {/* ── Section 8 ── */}
        <Header>8. Your Privacy Rights</Header>
        <TextSection>
          Depending on your jurisdiction, you may have the right to:
        </TextSection>
        <List>
          <ListItem>
            Access the personal information we hold about you and request a
            copy.
          </ListItem>
          <ListItem>
            Correct inaccurate or incomplete personal information.
          </ListItem>
          <ListItem>
            Request deletion of your personal information, subject to legal
            retention requirements.
          </ListItem>
          <ListItem>
            Opt out of certain data processing activities, including SMS
            marketing (note: operational dispatch SMS is tied to service
            delivery and may not be fully disabled without discontinuing
            certain features).
          </ListItem>
          <ListItem>
            Lodge a complaint with a supervisory authority if you believe your
            privacy rights have been violated.
          </ListItem>
        </List>
        <TextSection>
          To exercise any of these rights, contact us at{" "}
          <a href={`mailto:${APP_SUPPORT_EMAIL}`}>{APP_SUPPORT_EMAIL}</a>.
          We will respond to verifiable requests within the timeframes required
          by applicable law (typically 30 to 45 days).
        </TextSection>

        {/* ── Section 9 ── */}
        <Header>9. California Residents: CCPA Notice</Header>
        <TextSection>
          If you are a California resident, you have additional rights under
          the California Consumer Privacy Act (CCPA), as amended by the
          California Privacy Rights Act (CPRA). These include the right to
          know what personal information we collect and how it is used, the
          right to delete personal information, the right to correct personal
          information, and the right to opt out of the sale or sharing of
          personal information. <strong>{APP_NAME} does not sell personal
          information.</strong> To submit a CCPA request, contact us at{" "}
          <a href={`mailto:${APP_SUPPORT_EMAIL}`}>{APP_SUPPORT_EMAIL}</a>.
        </TextSection>

        {/* ── Section 10 ── */}
        <Header>10. Children&apos;s Privacy</Header>
        <TextSection>
          The Services are not directed to individuals under the age of 18.
          We do not knowingly collect personal information from children under
          18. If we become aware that a child under 18 has provided personal
          information to us, we will take steps to delete that information
          promptly.
        </TextSection>

        {/* ── Section 11 ── */}
        <Header>11. Changes to This Privacy Policy</Header>
        <TextSection>
          We may update this Privacy Policy from time to time. When we make
          material changes, we will update the "Last Updated" date at the
          top of this page and, where appropriate, provide in-app notification.
          Your continued use of the Services after any such changes constitutes
          your acceptance of the revised policy.
        </TextSection>

        {/* ── Section 12 ── */}
        <Header>12. Contact Us</Header>
        <TextSection>
          If you have questions about this Privacy Policy, our data practices,
          or your rights, please contact us at:{" "}
          <a href={`mailto:${APP_SUPPORT_EMAIL}`}>{APP_SUPPORT_EMAIL}</a>.
        </TextSection>
        <TextSection>
          Our full Terms of Service are available at{" "}
          <a
            href={`https://${APP_URL}/terms`}
            target="_blank"
            rel="noopener noreferrer"
          >
            https://{APP_URL}/terms
          </a>
          .
        </TextSection>

      </TextContainer>
    </Container>
  );
}

const Container = styled.div`
  color: black;
  font-size: 13px;
  font-weight: 400;
  line-height: 20.8px;
  text-align: left;
  width: 80%;
  margin: auto;
  overflow: hidden;
  margin-bottom: 40px;
  margin-top: 3rem;

  a {
    color: #1a73e8;
    font-weight: 500;
    text-decoration: none;
  }

  a:hover {
    color: #0d47a1;
    text-decoration: underline;
  }
`;

const TextContainer = styled.div`
  height: 100%;
  overflow: unset !important;
  padding-right: 5%;
  padding-bottom: 32px;
`;

const Header = styled.div`
  font-size: 13px;
  font-weight: bold;
  line-height: 20.8px;
  margin-top: 24px;
`;

const MainHeader = styled.div`
  text-align: center;
  font-size: 22px;
  font-style: normal;
  font-weight: 700;
  margin-bottom: 18px;
`;

const List = styled.ul`
  font-size: 13px;
  font-weight: 400;
  line-height: 20.8px;
  margin-top: 18px;
`;

const ListItem = styled.li`
  font-size: 13px;
  font-weight: 400;
  line-height: 20.8px;
  margin-top: 10px;
`;

const TextSection = styled.div`
  font-size: 13px;
  font-weight: 400;
  line-height: 20.8px;
  margin-top: 18px;
  text-transform: ${(props) => (props.$capitalize ? "uppercase" : "none")};
`;
